<template>
  <div class="park-admin-onboarding">
    <v-container class="fill-height pa-0" fluid>
      <v-layout align-center justify-center fill-height>
        <v-flex xs12 sm10 md8 lg6 xl5>
          <v-card class="elevation-4">
            <!-- Header -->
            <v-toolbar flat color="primary" dark class="rounded-top">
              <v-icon left large>{{ isBuilding ? 'apartment' : 'business' }}</v-icon>
              <v-toolbar-title class="headline font-weight-bold">
                {{ isBuilding ? 'Onboarding Administrador de Edificio' : 'Onboarding Administrador de Parque' }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>

            <!-- Barra de Progreso -->
            <div class="progress-section pa-4 border-bottom">
              <div class="progress-bar mb-2">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
              <p class="caption text-center mb-0">{{ progressText }}</p>
            </div>

            <!-- Contenido Principal -->
            <v-card-text class="pa-6">
              <!-- Error: Link no válido o ya utilizado -->
              <div v-if="showError && !tokenValid" class="text-center py-8">
                <v-icon size="80" color="error" class="mb-4">error_outline</v-icon>
                <h2 class="headline font-weight-bold mb-4">{{ errorMessage }}</h2>
                <p class="body-1 text-muted mb-6">
                  Si crees que esto es un error, solicita un nuevo link al administrador que te invitó.
                </p>
                <v-btn color="primary" outlined href="/">
                  <v-icon left>home</v-icon>
                  Volver al Inicio
                </v-btn>
              </div>

              <!-- Paso 1: Validación de Token y Datos del Parque -->
              <div v-if="currentStep === 1 && tokenValid" class="step-container">
                <div class="step-title mb-6">
                  <v-icon color="primary" class="mr-2" large>check_circle</v-icon>
                  <span class="title">Información de la Invitación</span>
                </div>

                <!-- Información del Parque/Edificio (Read-only) -->
                <v-card class="mb-6 elevation-0" style="background: #f5f5f5; border-left: 4px solid #1976d2;">
                  <v-card-text class="pa-4">
                    <p class="caption mb-3 text-muted font-weight-bold">{{ isBuilding ? 'INFORMACIÓN DEL EDIFICIO' : 'INFORMACIÓN DEL PARQUE' }}</p>
                    <div class="info-item mb-3">
                      <span class="caption text-muted">{{ isBuilding ? 'Nombre del Edificio:' : 'Nombre del Parque:' }}</span>
                      <p class="body-2 mb-0 font-weight-bold">{{ parkName }}</p>
                    </div>
                    <div class="info-item mb-3">
                      <span class="caption text-muted">Tipo:</span>
                      <p class="body-2 mb-0">{{ locationType }}</p>
                    </div>
                    <div class="info-item mb-3">
                      <span class="caption text-muted">Email de Invitación:</span>
                      <p class="body-2 mb-0">{{ invitationEmail }}</p>
                    </div>
                    <div class="info-item">
                      <span class="caption text-muted">Expira en:</span>
                      <p class="body-2 mb-0">{{ expiresAt }}</p>
                    </div>
                  </v-card-text>
                </v-card>

                <div class="text-center">
                  <v-btn
                    color="primary"
                    large
                    depressed
                    @click="goToStep(2)"
                  >
                    Siguiente <v-icon right>arrow_forward</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Paso 2: Formulario de Datos Personales -->
              <div v-if="currentStep === 2 && tokenValid" class="step-container">
                <div class="step-title mb-6">
                  <v-icon color="primary" class="mr-2" large>edit</v-icon>
                  <span class="title">Datos Personales</span>
                </div>

                <v-form ref="formStep2" @submit.prevent="submitStep2">
                  <v-text-field
                    v-model="form.first_name"
                    label="Nombre *"
                    outlined
                    dense
                    class="mb-4"
                    prepend-icon="person"
                    :error-messages="errors.first_name"
                    @input="clearError('first_name')"
                    maxlength="150"
                  ></v-text-field>

                  <v-text-field
                    v-model="form.last_name"
                    label="Apellidos *"
                    outlined
                    dense
                    class="mb-4"
                    prepend-icon="person"
                    :error-messages="errors.last_name"
                    @input="clearError('last_name')"
                    maxlength="150"
                  ></v-text-field>

                  <div class="mb-4">
                    <label class="caption">Teléfono (10 dígitos mínimo) *</label>
                    <vue-tel-input
                      v-model="form.celular"
                      @onInput="handlePhoneInput"
                      class="mt-1"
                      placeholder="+1 (555) 123-4567"
                    ></vue-tel-input>
                    <div v-if="errors.celular" class="error-messages mt-1">
                      <span class="caption" style="color: #f44336;">{{ Array.isArray(errors.celular) ? errors.celular[0] : errors.celular }}</span>
                    </div>
                  </div>

                  <v-text-field
                    v-model="form.password"
                    label="Contraseña (mínimo 8 caracteres) *"
                    outlined
                    dense
                    type="password"
                    class="mb-4"
                    prepend-icon="lock"
                    :error-messages="errors.password"
                    @input="clearError('password')"
                    counter="100"
                    maxlength="100"
                  ></v-text-field>

                  <v-text-field
                    v-model="form.password_confirm"
                    label="Confirmar Contraseña *"
                    outlined
                    dense
                    type="password"
                    class="mb-6"
                    prepend-icon="lock"
                    :error-messages="errors.password_confirm"
                    @input="clearError('password_confirm')"
                  ></v-text-field>

                  <div class="d-flex gap-2">
                    <v-btn
                      color="default"
                      large
                      depressed
                      @click="goToStep(1)"
                      :disabled="loading"
                    >
                      <v-icon left>arrow_back</v-icon>
                      Atrás
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="primary"
                      large
                      depressed
                      type="submit"
                      :loading="loading"
                    >
                      Siguiente <v-icon right>arrow_forward</v-icon>
                    </v-btn>
                  </div>
                </v-form>
              </div>

              <!-- Paso 3: Carga de Documentos (INE) -->
              <div v-if="currentStep === 3 && tokenValid" class="step-container">
                <div class="step-title mb-6">
                  <v-icon color="primary" class="mr-2" large>attach_file</v-icon>
                  <span class="title">Documentos de Identificación</span>
                </div>

                <v-alert type="info" outlined dense class="mb-4">
                  Los documentos son obligatorios. Formatos permitidos: JPG o PNG, máximo 5MB por archivo.
                </v-alert>

                <!-- INE Frente -->
                <div class="document-upload-section mb-6">
                  <v-card class="elevation-0 border">
                    <v-card-text class="pa-4">
                      <div class="d-flex align-start mb-4">
                        <div class="flex-grow-1">
                          <p class="body-2 font-weight-bold mb-1">INE - Frente *</p>
                          <p class="caption text-muted mb-2">Fotografía del lado frontal de tu identificación</p>
                        </div>
                        <v-chip
                          v-if="files.ine_frente"
                          small
                          color="success"
                          text-color="white"
                        >
                          <v-icon left small>check_circle</v-icon>
                          Cargado
                        </v-chip>
                        <v-chip
                          v-else
                          small
                          color="error"
                          text-color="white"
                        >
                          Obligatorio
                        </v-chip>
                      </div>

                      <div v-if="filePreviews.ine_frente" class="text-center mb-3">
                        <img :src="filePreviews.ine_frente" class="doc-preview" alt="INE Frente">
                      </div>

                      <div
                        v-if="!filePreviews.ine_frente"
                        @click="triggerFileInput('ine_frente')"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="handleFileDrop($event, 'ine_frente')"
                        :class="['drag-drop-zone', { 'dragging': isDragging }]"
                      >
                        <v-icon size="40" color="primary" class="mb-2">cloud_upload</v-icon>
                        <p class="subtitle-2 mb-1">Arrastra la imagen aquí</p>
                        <p class="caption text-muted mb-2">o haz clic para seleccionar</p>
                        <p class="caption primary--text">JPG o PNG (máximo 5MB)</p>
                      </div>

                      <input
                        ref="fileInput_ine_frente"
                        type="file"
                        @change="(e) => handleFileSelect(e, 'ine_frente')"
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                        style="display: none"
                      />

                      <v-alert v-if="errors.ine_frente" type="error" class="mt-4" outlined dense>
                        {{ Array.isArray(errors.ine_frente) ? errors.ine_frente[0] : errors.ine_frente }}
                      </v-alert>

                      <div v-if="files.ine_frente" class="mt-4 text-center">
                        <v-chip
                          color="success"
                          text-color="white"
                          small
                          class="mr-2"
                        >
                          <v-icon left small>check_circle</v-icon>
                          {{ files.ine_frente.name }}
                        </v-chip>
                        <v-btn
                          icon
                          x-small
                          @click="removeFile('ine_frente')"
                        >
                          <v-icon x-small>close</v-icon>
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- INE Atrás -->
                <div class="document-upload-section mb-6">
                  <v-card class="elevation-0 border">
                    <v-card-text class="pa-4">
                      <div class="d-flex align-start mb-4">
                        <div class="flex-grow-1">
                          <p class="body-2 font-weight-bold mb-1">INE - Reverso *</p>
                          <p class="caption text-muted mb-2">Fotografía del lado posterior de tu identificación</p>
                        </div>
                        <v-chip
                          v-if="files.ine_atras"
                          small
                          color="success"
                          text-color="white"
                        >
                          <v-icon left small>check_circle</v-icon>
                          Cargado
                        </v-chip>
                        <v-chip
                          v-else
                          small
                          color="error"
                          text-color="white"
                        >
                          Obligatorio
                        </v-chip>
                      </div>

                      <div v-if="filePreviews.ine_atras" class="text-center mb-3">
                        <img :src="filePreviews.ine_atras" class="doc-preview" alt="INE Atrás">
                      </div>

                      <div
                        v-if="!filePreviews.ine_atras"
                        @click="triggerFileInput('ine_atras')"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="handleFileDrop($event, 'ine_atras')"
                        :class="['drag-drop-zone', { 'dragging': isDragging }]"
                      >
                        <v-icon size="40" color="primary" class="mb-2">cloud_upload</v-icon>
                        <p class="subtitle-2 mb-1">Arrastra la imagen aquí</p>
                        <p class="caption text-muted mb-2">o haz clic para seleccionar</p>
                        <p class="caption primary--text">JPG o PNG (máximo 5MB)</p>
                      </div>

                      <input
                        ref="fileInput_ine_atras"
                        type="file"
                        @change="(e) => handleFileSelect(e, 'ine_atras')"
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                        style="display: none"
                      />

                      <v-alert v-if="errors.ine_atras" type="error" class="mt-4" outlined dense>
                        {{ Array.isArray(errors.ine_atras) ? errors.ine_atras[0] : errors.ine_atras }}
                      </v-alert>

                      <div v-if="files.ine_atras" class="mt-4 text-center">
                        <v-chip
                          color="success"
                          text-color="white"
                          small
                          class="mr-2"
                        >
                          <v-icon left small>check_circle</v-icon>
                          {{ files.ine_atras.name }}
                        </v-chip>
                        <v-btn
                          icon
                          x-small
                          @click="removeFile('ine_atras')"
                        >
                          <v-icon x-small>close</v-icon>
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <div class="d-flex gap-2 mt-6">
                  <v-btn
                    color="default"
                    large
                    depressed
                    @click="goToStep(2)"
                    :disabled="loading"
                  >
                    <v-icon left>arrow_back</v-icon>
                    Atrás
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    large
                    depressed
                    @click="submitStep3"
                    :loading="loading"
                  >
                    Siguiente
                    <v-icon right>arrow_forward</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Paso 4: Confirmación y Términos -->
              <div v-if="currentStep === 4 && tokenValid" class="step-container">
                <div class="step-title mb-6">
                  <v-icon color="primary" class="mr-2" large>verified_user</v-icon>
                  <span class="title">Confirmar y Completar</span>
                </div>

                <!-- Resumen -->
                <v-card class="mb-6 elevation-0 border">
                  <v-card-text class="pa-4">
                    <p class="subtitle-2 font-weight-bold mb-4 primary--text">Resumen de Datos</p>
                    <div class="summary-item mb-3">
                      <span class="caption text-muted">Nombre:</span>
                      <p class="body-2 mb-0">{{ form.first_name }} {{ form.last_name }}</p>
                    </div>
                    <div class="summary-item mb-3">
                      <span class="caption text-muted">Teléfono:</span>
                      <p class="body-2 mb-0">{{ form.celular }}</p>
                    </div>
                    <div class="summary-item mb-3">
                      <span class="caption text-muted">Email:</span>
                      <p class="body-2 mb-0">{{ invitationEmail }}</p>
                    </div>
                    <div class="summary-item">
                      <span class="caption text-muted">Documentos:</span>
                      <p class="body-2 mb-1">✓ INE Frente: {{ files.ine_frente ? files.ine_frente.name : 'Faltante' }}</p>
                      <p class="body-2 mb-0">✓ INE Reverso: {{ files.ine_atras ? files.ine_atras.name : 'Faltante' }}</p>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Checkbox de Consentimiento -->
                <v-checkbox
                  v-model="consent"
                  class="mb-6"
                  :error-messages="errors.consent"
                  @change="clearError('consent')"
                >
                  <template v-slot:label>
                    <span class="caption">Confirmo que los datos son correctos y autorizo su procesamiento *</span>
                  </template>
                </v-checkbox>

                <!-- Errores Generales -->
                <v-alert v-if="errors.general" type="error" class="mb-4" outlined>
                  {{ errors.general }}
                </v-alert>

                <div class="d-flex gap-2">
                  <v-btn
                    color="default"
                    large
                    depressed
                    @click="goToStep(3)"
                    :disabled="loading"
                  >
                    <v-icon left>arrow_back</v-icon>
                    Atrás
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="success"
                    large
                    depressed
                    @click="submitStep4"
                    :loading="loading"
                    :disabled="!consent"
                  >
                    Completar Onboarding
                    <v-icon right>check_circle</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Paso 5: Éxito -->
              <div v-if="currentStep === 5" class="step-container text-center">
                <v-icon size="80" color="success" class="mb-4">check_circle</v-icon>
                <h2 class="headline font-weight-bold mb-2">¡Bienvenido a Intrare!</h2>

                <v-alert type="success" class="mb-6" outlined icon="info">
                  <strong>Tu cuenta ha sido creada exitosamente.</strong><br>
                  Ya puedes iniciar sesión como administrador{{ isBuilding ? ' del edificio' : ' del parque' }} con tus credenciales
                </v-alert>

                <v-card class="mb-6 elevation-0 border">
                  <v-card-text class="pa-4">
                    <p class="subtitle-2 font-weight-bold mb-3">Tu Usuario:</p>
                    <p class="body-2 text-monospace mb-0">{{ invitationEmail }}</p>
                  </v-card-text>
                </v-card>

                <p class="caption text-muted mb-6">¡Gracias por usar nuestro servicio!</p>

                <v-btn color="primary" large depressed href="/">
                  <v-icon left>home</v-icon>
                  Ir al Inicio de Sesión
                </v-btn>
              </div>

              <!-- Errores Generales -->
              <v-alert v-if="showError && tokenValid" type="error" class="mt-4" closable @input="showError = false">
                {{ errorMessage }}
              </v-alert>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" bottom right>
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text small v-bind="attrs" @click="showSnackbar = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'ParkAdminOnboarding',
  data() {
    return {
      token: null,
      currentStep: 1,
      loading: false,
      isDragging: false,

      tokenValid: false,
      parkId: null,
      parkName: '',
      invitationEmail: '',
      expiresAt: '',
      isBuilding: false,
      locationType: 'Parque',

      form: {
        first_name: '',
        last_name: '',
        celular: '',
        password: '',
        password_confirm: ''
      },

      files: {
        ine_frente: null,
        ine_atras: null
      },
      filePreviews: {
        ine_frente: null,
        ine_atras: null
      },

      consent: false,
      errors: {},
      showError: false,
      errorMessage: '',
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    progressPercentage() {
      const percentages = {
        1: 20,
        2: 40,
        3: 60,
        4: 80,
        5: 100
      }
      return percentages[this.currentStep] || 0
    },
    progressText() {
      const texts = {
        1: 'Paso 1: Información (20%)',
        2: 'Paso 2: Datos Personales (40%)',
        3: 'Paso 3: Documentos (60%)',
        4: 'Paso 4: Confirmación (80%)',
        5: '¡Completado! (100%)'
      }
      return texts[this.currentStep] || ''
    }
  },
  mounted() {
    this.token = this.$route.params.token
    if (!this.token) {
      this.showErrorMessage('Token no válido')
      return
    }
    this.validateToken()
  },
  methods: {
    async validateToken() {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/AdminPark/onboarding/validate-token/`,
          { token: this.token },
          { timeout: 5000 }
        )

        if (response.data.valid) {
          this.tokenValid = true
          this.parkId = response.data.park_id
          this.parkName = response.data.park_name || 'Parque'
          this.invitationEmail = response.data.email || ''
          this.expiresAt = this.formatDate(response.data.expires_at)
          this.isBuilding = response.data.is_building || false
          this.locationType = response.data.location_type || 'Parque'
        } else {
          this.showErrorMessage('Token no válido')
        }
      } catch (error) {
        if (error.response && error.response.status === 400) {
          const errorData = error.response.data
          if (Array.isArray(errorData.token)) {
            this.showErrorMessage(errorData.token[0])
          } else {
            this.showErrorMessage('El token es inválido o ha expirado')
          }
        } else {
          this.showErrorMessage('Error al validar el token. Por favor intenta más tarde.')
        }
      }
    },
    submitStep2() {
      this.errors = {}

      if (!this.form.first_name || this.form.first_name.length < 2) {
        this.errors.first_name = 'El nombre debe tener al menos 2 caracteres'
      }
      if (!this.form.last_name || this.form.last_name.length < 2) {
        this.errors.last_name = 'Los apellidos deben tener al menos 2 caracteres'
      }
      if (!this.form.celular || this.form.celular.replace(/\D/g, '').length < 10) {
        this.errors.celular = 'El teléfono debe tener al menos 10 dígitos'
      }
      if (!this.form.password || this.form.password.length < 8) {
        this.errors.password = 'La contraseña debe tener al menos 8 caracteres'
      }
      if (this.form.password !== this.form.password_confirm) {
        this.errors.password_confirm = 'Las contraseñas no coinciden'
      }

      if (Object.keys(this.errors).length > 0) {
        return
      }

      this.goToStep(3)
      this.showSnackbarMessage('Datos validados correctamente', 'success')
    },
    submitStep3() {
      if (!this.files.ine_frente || !this.files.ine_atras) {
        this.showSnackbarMessage('Ambos documentos de identificación son obligatorios', 'error')
        return
      }
      this.goToStep(4)
    },
    async submitStep4() {
      if (!this.consent) {
        this.errors.consent = 'Debe confirmar que los datos son correctos'
        return
      }

      const faltantes = Object.keys(this.files).filter(key => !this.files[key])
      if (faltantes.length > 0) {
        this.showSnackbarMessage('Faltan documentos obligatorios', 'error')
        this.goToStep(3)
        return
      }

      this.errors = {}
      this.loading = true

      try {
        const formData = new FormData()
        formData.append('token', this.token)
        formData.append('park_id', this.parkId)
        formData.append('first_name', this.form.first_name)
        formData.append('last_name', this.form.last_name)
        formData.append('celular', this.form.celular)
        formData.append('password', this.form.password)
        formData.append('password_confirm', this.form.password_confirm)
        formData.append('ine_frente', this.files.ine_frente)
        formData.append('ine_atras', this.files.ine_atras)

        const response = await axios.post(
          `${API_BASE_URL}/AdminPark/onboarding/complete/`,
          formData,
          { timeout: 10000 }
        )

        if (response.status === 201 || response.data.message) {
          this.goToStep(5)
          this.showSnackbarMessage('¡Cuenta creada exitosamente!', 'success')
        }
      } catch (error) {
        const data = error.response && error.response.data

        if (error.response && error.response.status === 400 && data) {
          this.errors = data
          this.showSnackbarMessage('Por favor revisa los errores y intenta nuevamente', 'error')
        } else {
          this.showSnackbarMessage('Error al crear la cuenta. Por favor intenta más tarde.', 'error')
        }
      } finally {
        this.loading = false
      }
    },
    triggerFileInput(key) {
      this.$refs[`fileInput_${key}`].click()
    },
    handleFileSelect(event, key) {
      const file = event.target.files[0]
      if (file) {
        this.setFile(file, key)
      }
    },
    handleFileDrop(event, key) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file) {
        this.setFile(file, key)
      }
    },
    setFile(file, key) {
      delete this.errors[key]

      const allowedTypes = ['image/jpeg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        this.$set(this.errors, key, 'Tipo de archivo no permitido. Usa JPG o PNG')
        return
      }

      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        this.$set(this.errors, key, 'El archivo no puede exceder 5MB')
        return
      }

      this.$set(this.files, key, file)

      const reader = new FileReader()
      reader.onload = (e) => {
        this.$set(this.filePreviews, key, e.target.result)
      }
      reader.readAsDataURL(file)

      this.showSnackbarMessage('Documento cargado correctamente', 'success')
    },
    removeFile(key) {
      this.$set(this.files, key, null)
      this.$set(this.filePreviews, key, null)
      if (this.$refs[`fileInput_${key}`]) {
        this.$refs[`fileInput_${key}`].value = null
      }
    },
    goToStep(step) {
      this.currentStep = step
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    },
    clearError(field) {
      delete this.errors[field]
    },
    showErrorMessage(message) {
      this.showError = true
      this.errorMessage = message
    },
    showSnackbarMessage(message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    handlePhoneInput({ number, isValid, country }) {
      if (number && number.e164) {
        this.form.celular = number.e164
      }
      if (this.form.celular) {
        const digitos = this.form.celular.replace(/\D/g, '')
        if (digitos.length < 10) {
          this.errors.celular = 'El teléfono debe tener al menos 10 dígitos'
        } else {
          this.clearError('celular')
        }
      }
    }
  }
}
</script>

<style scoped>
.park-admin-onboarding {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 40px 0;
}

.gap-2 {
  gap: 8px;
}

.error-messages {
  display: block;
}

.caption {
  font-size: 12px;
}

.mt-1 {
  margin-top: 8px;
}

.justify-center {
  justify-content: center;
}

.flex-wrap {
  flex-wrap: wrap;
}

.border {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.border-left-primary {
  border-left: 4px solid #002b48 !important;
}

.primary-border {
  border: 2px solid #002b48 !important;
}

.step-title {
  display: flex;
  align-items: center;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #002b48;
  transition: width 0.3s ease;
}

.drag-drop-zone {
  border: 2px dashed #002b48;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f9f9f9;
}

.drag-drop-zone:hover,
.drag-drop-zone.dragging {
  border-color: #002b48;
  background: #f0f8ff;
  box-shadow: 0 4px 12px rgba(0, 43, 72, 0.1);
}

.doc-preview {
  max-width: 100%;
  max-height: 180px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
}

.summary-item {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
}

.summary-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-box {
  background: #f5f5f5;
  border-radius: 4px;
}

.info-box ul {
  list-style: none;
  padding: 0;
}

.info-box li {
  color: #555;
}

.text-muted {
  color: #999;
}

.d-flex {
  display: flex;
}

.align-start {
  align-items: flex-start;
}

.rounded-top {
  border-radius: 4px 4px 0 0;
}

.step-container {
  animation: fadeIn 0.3s ease;
}

.info-item {
  padding: 8px 0;
}

.text-monospace {
  font-family: 'Courier New', monospace;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
